// src/data/servicesData.js

export const services = [
    {
        slug: "trailer-repair-fleet-maintenance",
        navLabel: "Trailer Repair & Fleet Maintenance",
        kicker: "Fleet Repair",
        title: "Trailer Repair & Fleet Maintenance",
        short:
            "Trailer repair, maintenance, structural work, welding, and recurring support for commercial trailers and fleets.",
        hero:
            "South Trailers supports commercial fleets with trailer repair, preventive maintenance, compliance-related repairs, structural work, welding, and recurring service programs built around uptime.",
        image: "/images/Services/S1.jpeg",
        imagePosition: "object-[center_45%]",
        sections: [
            {
                title: "Built for fleet uptime",
                body:
                    "This service covers the repair and maintenance work commercial fleets need to keep trailers safe, compliant, and moving.",
            },
            {
                title: "Common repair areas",
                body:
                    "We handle brake-related work, air systems, suspension, airbags, lighting, roof issues, structural repairs, welding, landing gear, and general trailer service.",
            },
            {
                title: "Recurring fleet support",
                body:
                    "Customers can use this service for one repair, multiple units, preventive maintenance, inspection cycles, or ongoing fleet support.",
            },
        ],
        bullets: [
            "Trailer structural repairs",
            "Welding and heavy-duty repair work",
            "Brake, air, suspension, lighting, and landing gear work",
            "Preventive maintenance support",
            "Recurring fleet service programs",
        ],
        cta: "Schedule Fleet Service",
    },
    {
        slug: "mobile-fleet-service",
        navLabel: "Mobile Fleet Service",
        kicker: "Mobile Service",
        title: "Mobile Fleet Service",
        short:
            "On-site trailer repair and maintenance at fleet yards, warehouses, terminals, and customer facilities.",
        hero:
            "Mobile fleet service is built for operations that need trailer service directly at the fleet location.",
        image: "/images/Services/S2.jpeg",
        imagePosition: "object-[center_50%]",
        sections: [
            {
                title: "Service at your location",
                body:
                    "We service trailers at fleet yards, warehouses, trucking terminals, distribution centers, and customer facilities. This reduces unnecessary equipment movement and helps fleets handle multiple units more efficiently.",
            },
            {
                title: "What mobile service covers",
                body:
                    "Mobile service can include trailer repair, DOT-related repairs, PM inspections, lighting repairs, air system issues, suspension work, and emergency fleet support.",
            },
            {
                title: "When mobile becomes in-shop",
                body:
                    "Some repairs require more time, heavier work, or controlled shop conditions. When that happens, we schedule the unit for in-shop service instead of forcing the wrong repair environment.",
            },
        ],
        bullets: [
            "On-site trailer repair",
            "Fleet yard service",
            "Mobile DOT-related repairs",
            "Mobile PM inspections",
            "Multiple-unit fleet support",
        ],
        cta: "Schedule Mobile Service",
    },
    {
        slug: "in-shop-trailer-service",
        navLabel: "In-Shop Trailer Service",
        kicker: "Scheduled Service",
        title: "In-Shop Trailer Service",
        short:
            "Scheduled trailer service at our location for jobs that need more time, tools, or controlled working conditions.",
        hero:
            "In-shop service gives customers a scheduled option for trailers that need longer repair windows, detailed work, or controlled working conditions.",
        image: "/images/Services/S8.jpeg",
        imagePosition: "object-[center_50%]",
        cardImage: "/images/Services/S8.jpeg",
        cardImagePosition: "object-[center_50%]",
        sections: [
            {
                title: "Scheduled trailer service",
                body:
                    "Customers can bring trailers to our location for scheduled repair, inspection, maintenance, and heavier service work.",
            },
            {
                title: "Controlled repair environment",
                body:
                    "In-shop service is useful for jobs that need more time, tools, space, welding, structural work, or a controlled setup.",
            },
            {
                title: "Fleet-focused execution",
                body:
                    "The goal is the same as our mobile service: clear repair work, practical timelines, and support for keeping fleet equipment operational.",
            },
        ],
        bullets: [
            "Scheduled trailer drop-off",
            "Longer repair windows",
            "Structural or detailed repair work",
            "Controlled service environment",
            "Fleet maintenance support",
        ],
        cta: "Schedule In-Shop Service",
    },
    {
        slug: "dot-pm-programs",
        navLabel: "DOT & PM Programs",
        kicker: "Compliance & Maintenance",
        title: "DOT & PM Inspections",
        short:
            "DOT compliance support and preventive maintenance inspections for recurring fleet readiness.",
        hero:
            "DOT and PM services help fleets identify safety, compliance, and maintenance issues before they create downtime.",
        image: "/images/Services/S9.jpeg",
        imagePosition: "object-[center_50%]",
        sections: [
            {
                title: "DOT compliance support",
                body:
                    "DOT work is about roadworthiness and safety. If a trailer has issues that prevent it from being inspection-ready, we help identify and repair those problems.",
            },
            {
                title: "Preventive maintenance",
                body:
                    "PM is not the same as a DOT annual inspection. PM is a preventive check designed to catch issues early before they create downtime or more expensive repairs.",
            },
            {
                title: "Fleet inspection cycles",
                body:
                    "DOT and PM services can support annual DOT cycles, quarterly PM schedules, recurring mobile visits, and ongoing fleet readiness.",
            },
        ],
        bullets: [
            "DOT compliance-related repairs",
            "PM inspections",
            "Safety corrections",
            "Fleet readiness support",
            "Recurring inspection cycles",
        ],
        cta: "Schedule DOT / PM Support",
    },
    {
        slug: "roadside-assistance",
        navLabel: "Roadside Assistance",
        kicker: "Emergency Support",
        title: "Roadside Assistance",
        short:
            "Emergency trailer repair and field support when trailer issues interrupt operations.",
        hero:
            "Roadside assistance is for urgent trailer issues that need fast response, practical repair, and operational judgment.",
        image: "/images/Services/S4.jpeg",
        imagePosition: "object-[center_45%]",
        sections: [
            {
                title: "Emergency trailer support",
                body:
                    "When a trailer issue stops operations, the priority is getting the unit safe, functional, or properly routed for a full repair.",
            },
            {
                title: "Common roadside issues",
                body:
                    "Roadside work can involve lighting problems, air or brake-related issues, suspension problems, trailer damage, or temporary operational fixes.",
            },
            {
                title: "Field repair support",
                body:
                    "Roadside assistance gives fleets a direct response option when trailer problems happen outside a normal shop schedule.",
            },
        ],
        bullets: [
            "Emergency trailer repair",
            "Lighting and electrical issues",
            "Air and brake-related problems",
            "Suspension issues",
            "Temporary operational fixes",
        ],
        cta: "Request Roadside Help",
    },
    {
        slug: "curtain-side-services",
        navLabel: "Curtain-Side Services",
        kicker: "Side-Curtain Service",
        title: "Curtain-Side Manufacturing & Repair",
        short:
            "Curtain-side manufacturing, repair, installation, hardware, rollers, tracks, and related components.",
        hero:
            "South Trailers provides curtain-side manufacturing, repair, installation, hardware service, rollers, tracks, buckles, straps, and related curtain-side components.",
        image: "/images/Banners/South_Trailers_banner.png",
        imagePosition: "object-center",
        sections: [
            {
                title: "Curtain-side manufacturing and repair",
                body:
                    "We handle curtain-side manufacturing, installation, repair, replacement, and maintenance for commercial trailer equipment.",
            },
            {
                title: "Hardware and components",
                body:
                    "Curtain-side work can include rollers, tracks, buckles, straps, hooks, rails, and related hardware.",
            },
            {
                title: "Service matched to the job",
                body:
                    "Curtain-side work can be handled through mobile service or scheduled service depending on the repair, installation, and hardware needs.",
            },
        ],
        bullets: [
            "Curtain-side manufacturing",
            "Curtain repair and replacement",
            "Rollers, tracks, buckles, straps, and hardware",
            "Mobile or scheduled service depending on the job",
            "Curtain system support for commercial trailers",
        ],
        cta: "Schedule Curtain Service",
    },
];

export const getServiceBySlug = (slug) => {
    return services.find((service) => service.slug === slug);
};
